import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import companyDiaryApi from '@/api/companyDiary';
import { useUserStore } from '@store/user';
import { toast } from 'vue-sonner';
import type { Ref } from 'vue';

/**
 * The note thread on one company diary entry.
 *
 * The same shape as {@see useSalesActionNotes}, against the diary's routes. Both
 * threads hang off a `ScheduleService` and are served by `NoteResource`, so the
 * row types are identical - but the two composables stay separate rather than one
 * being parameterised by URL, because the edit permission differs (the diary also
 * requires `company-diary-modify`) and merging them would put a diary rule inside
 * a sales surface.
 *
 * **Query key.** `['company-diary-notes', entryId, tenantId]`. The tenant id
 * trails it because entry ids are tenant-scoped auto-increments: entry 7 exists in
 * every tenant, and without it a tenant switch would serve one tenant's thread
 * against another's entry.
 */
export const COMPANY_DIARY_NOTES_KEY = 'company-diary-notes';

export interface CompanyDiaryNoteMention {
    id: number;
    fullname: string | null;
    avatar: string | null;
}

/** The `NoteResource` fields the diary thread reads. */
export interface CompanyDiaryNote {
    id: number;
    comment: string | null;
    type: string;
    /** The root this note replies to, or null when it is itself a root. */
    parent_id: number | null;
    is_reply: boolean;
    /**
     * Present on roots only. Threads are one level deep, so a reply carries no
     * `replies` key at all - that absence is what marks it as a leaf.
     */
    replies?: CompanyDiaryNote[];
    agent_name: string | null;
    user_id: number | null;
    author: { id: number; fullname: string | null; avatar: string | null } | null;
    mentioned_users: CompanyDiaryNoteMention[];
    mentioned_user_ids: number[];
    created_at: string | null;
    created_at_date_formatted: string | null;
    created_at_time_formatted: string | null;
    updated_at: string | null;
}

export interface CompanyDiaryNotePayload {
    comment: string;
    mentioned_user_ids?: number[];
    /**
     * Omitted for a new root. When set it must be the id of a root note in this
     * same thread - the API rejects a reply to a reply with a 422 rather than
     * re-parenting it.
     */
    parent_id?: number;
}

export interface CompanyDiaryNoteEdit {
    id: number;
    comment: string;
    mentioned_user_ids?: number[];
}

export function useCompanyDiaryNotes(entryId: Ref<number | null | undefined>) {
    const userStore = useUserStore();
    const queryClient = useQueryClient();

    const queryKey = computed(() => [COMPANY_DIARY_NOTES_KEY, entryId.value, userStore.tenantId]);

    /**
     * Only fetched once an entry is open and scheduled. An unscheduled entry has no
     * service for the thread to hang off, and the endpoint returns nothing for one.
     */
    const enabled = computed(() => Boolean(entryId.value));

    const invalidate = () => queryClient.invalidateQueries({ queryKey: [COMPANY_DIARY_NOTES_KEY, entryId.value] });

    const { data, isLoading, isFetching, refetch, error } = useQuery({
        queryKey,
        queryFn: async (): Promise<CompanyDiaryNote[]> => {
            const { data: response } = await companyDiaryApi.indexNotes(entryId.value!);

            return response.data ?? [];
        },
        enabled,
        retry: false,
        placeholderData: [],
        staleTime: 5000,
    });

    /**
     * No optimistic row: the author, the timestamps and the resolved mentions are
     * all decided server-side, so an invented row would have to fake every field
     * the thread renders.
     */
    const createNote = useMutation({
        mutationFn: async (payload: CompanyDiaryNotePayload): Promise<CompanyDiaryNote> => {
            const { data: response } = await companyDiaryApi.createNote(entryId.value!, {
                /** The thread is an internal operations record, so every note is an agent note. */
                type: 'AGENT_NOTE',
                ...payload,
            });

            return response.note;
        },
        onSuccess: invalidate,
        onError: (mutationError: { message?: string }) =>
            toast.error(mutationError?.message ?? 'Could not save the note.'),
    });

    /**
     * Same reasoning: no optimistic patch. The edit can be refused - the author
     * plus SUPER/ADMIN/OWNER, and nobody else - so a locally-applied comment would
     * show a change the server rejected until the next refetch put it back.
     */
    const updateNote = useMutation({
        mutationFn: async ({ id, ...payload }: CompanyDiaryNoteEdit): Promise<CompanyDiaryNote> => {
            const { data: response } = await companyDiaryApi.updateNote(entryId.value!, id, payload);

            return response.note;
        },
        onSuccess: invalidate,
        onError: (mutationError: { message?: string }) =>
            toast.error(mutationError?.message ?? 'Could not save the note.'),
    });

    /**
     * Whether the viewer may edit a given note, mirroring the API's
     * `orders-notes-actions-edit-delete` gate: its author, or an elevated role. The
     * server is still the authority - this only decides whether to offer the
     * control, so a refusal is never something the operator discovers by typing.
     */
    const canEdit = (note: CompanyDiaryNote): boolean => {
        const viewerId = Number(userStore.getUserId ?? 0);

        if (viewerId !== 0 && Number(note.user_id) === viewerId) {
            return true;
        }

        return ['SUPER', 'ADMIN', 'OWNER'].some((role) => userStore.hasRole(role));
    };

    return { notes: data, isLoading, isFetching, refetch, error, createNote, updateNote, canEdit };
}
